import { defineStore, storeToRefs } from "pinia";
import { ref, nextTick } from "vue";
import type { Dashboard, CreateDashboardRequest, WidgetOrchestrationResponse, WidgetManagementRequest } from "../types";
import type { WidgetCatalogItem } from "@/features/control-tower/interfaces/widget-catalogue";
import { type IResponse } from "@/api";
import { useDashboardManagement } from "@/features/control-tower/composables/useDashboardManagement";
import { useWidgetOrchestrator } from "@/features/control-tower/composables/useWidgetOrchestrator";
import { useWidgetCatalog } from "@/features/control-tower/composables/useWidgetCatalog";
import { logError, logInfo, logEvent, logMeasurement, FEATURE_NAMES, METRIC_TYPE } from "@/loggers";
import { useCreateDashboard } from "@/features/control-tower/composables/useCreateDashboard";
import { useDashboardRepositioning } from "@/features/control-tower/composables/useDashboardRepositioning";
import { debounce } from "@/global";

/**
 * Prefetches all lookup data for widget filters in the dashboard
 * Collects all unique combinations of dynamic endpoints and zone prefixes from widget catalog items
 * Each widget gets its own zone prefix for widget-based storage
 * @param widgetCatalogItems - Array of widget catalog items from the dashboard
 * @param zonePrefix - Zone prefix for storing lookups (if provided, uses this; otherwise uses "control-tower-{catalog_id}")
 */
const prefetchWidgetFilterLookups = async (widgetCatalogItems: any[], zonePrefix: string = ""): Promise<void> => {
  const lookupStore = useLookupStore();
  const uniqueLookups = new Map<string, string>(); // Map<"endpoint:zonePrefix", zonePrefix>

  for (const catalogItem of widgetCatalogItems) {
    // Determine zone prefix for this widget: use provided zonePrefix or construct from catalog_id
    const widgetZonePrefix = `${zonePrefix}-${catalogItem.catalog_id}` || `control-tower-${catalogItem.catalog_id}`;

    if (catalogItem.filters && Array.isArray(catalogItem.filters)) {
      for (const filter of catalogItem.filters) {
        if (filter.source === "DYNAMIC" && filter.dynamicSource?.endpoint) {
          const endpoint = filter.dynamicSource.endpoint;
          // Create unique key combining endpoint and zone prefix for widget-based storage
          const uniqueKey = `${endpoint}:${widgetZonePrefix}`;
          uniqueLookups.set(uniqueKey, widgetZonePrefix);
        }
      }
    }
  }

  const prefetchPromises = Array.from(uniqueLookups.entries()).map(([uniqueKey, widgetZonePrefix]) => {
    const endpoint = uniqueKey.split(":")[0]; // Extract endpoint from key
    return lookupStore.prefetchLookup(endpoint, widgetZonePrefix).catch((error) => {
      logError(
        FEATURE_NAMES.MVS_CONTROL_TOWER,
        `Failed to prefetch lookup for endpoint ${endpoint} with zone ${widgetZonePrefix}: ${error?.message || error}`
      );
    });
  });

  // Wait for all lookups, then merge results per zone
  await Promise.allSettled(prefetchPromises);
};
import { useFiltersStore, useLookupStore } from "@/store";
import { useZonePrefix } from "@/features/filters";
import { convertFilterForPayload, filterPayloadConverter } from "@/features/control-tower/util/filterPayloadConverter";
import { useUpdateWidget } from "@/features/control-tower/composables/useUpdateWidget";
import type { UpdateWidgetRequest, WidgetConfigItem } from "@/features/control-tower/interfaces/update-widget";
import { buildWidgetPayload } from "@/features/control-tower/util/commonUtils";
import { hyperlinkEnricher } from "@/features/control-tower/util/HyperlinkEnricher";

const BATCH_SIZE = 3;
const MAX_CONCURRENT_BATCHES = 5;
const REORDER_DEBOUNCE_MS = 1000; // Wait 1 second after last drag before saving

export const useDashboardStore = defineStore("dashboard", () => {
  const activeDashboardId = ref<string | null>(null);
  const activeWidgetId = ref<string | null>(null);
  const dashboards = ref<Dashboard[]>([]);
  const loading = ref<boolean>(false);
  const error = ref<string | null>(null);
  const isInitialized = ref<boolean>(false);
  const isAddDashboardModalOpen = ref<boolean>(false);
  const isManageDashboardModalOpen = ref<boolean>(false);
  const isCreatingDashboard = ref<boolean>(false);
  const isDeletingDashboard = ref<boolean>(false);
  const selectedWidgetCatalog = ref<WidgetCatalogItem[] | null>([]);
  const rawDashboardsData = ref<any[]>([]); // Store full dashboard data from API for update payloads

  const widgetsMeta = ref<any[]>([]);
  const widgetsData = ref<Record<string, any>>({});
  const widgetsLoading = ref<Record<string, boolean>>({});
  const widgetCatalog = ref<WidgetCatalogItem[]>([]);
  const loadedWidgetsDashboardId = ref<string | null>(null); // Track which dashboard widgets were loaded for
  const widgetsLoadDashboardId = ref<string | null>(null); // Track which dashboard load is current (latest switch wins)
  const isLoadingWidgets = ref<boolean>(false);
  const isLoadingCatalog = ref<boolean>(false);
  const isLoadingCatalogById = ref<boolean>(true);
  // Tracks the in-flight catalog fetch promise so concurrent callers (e.g. fast dashboard
  // switching) await the same request rather than returning immediately with an empty catalog.
  let catalogFetchPromise: Promise<void> | null = null;
  const filtersStore = useFiltersStore();
  const { bulkApplyFilters } = filtersStore;
  const { filtersApplied } = storeToRefs(filtersStore);
  const zonePrefix = ref<string>("");
  const widgetZonePrefix = ref<string>("");

  const widgetsError = ref<string | null>(null);
  const isLookupLoading = ref<boolean>(true);

  /**
   * Fetches all dashboards from the API using the composable
   * @param forceRefresh - If true, forces a refresh even if dashboards are already loaded or loading is in progress
   */
  const fetchDashboards = async (forceRefresh: boolean = false): Promise<IResponse | null> => {
    if (!forceRefresh && loading.value) {
      return null;
    }

    if (!forceRefresh && isInitialized.value && dashboards.value.length > 0) {
      return null;
    }

    try {
      loading.value = true;
      error.value = null;

      const { fetchDashboards: fetchDashboardsFromComposable } = useDashboardManagement();
      const response = await fetchDashboardsFromComposable();

      let apiDashboards: any[] = [];

      const responseData = response?.data || response;

      if (Array.isArray(responseData)) {
        apiDashboards = responseData;
      } else if (responseData && typeof responseData === "object") {
        if (responseData.data && Array.isArray(responseData.data.items)) {
          apiDashboards = responseData.data.items;
        } else if (Array.isArray(responseData.items)) {
          apiDashboards = responseData.items;
        } else if (Array.isArray(responseData.data)) {
          apiDashboards = responseData.data;
        }
      }

      dashboards.value = apiDashboards
        .map((dashboard: any) => ({
          id: dashboard.dashboard_id || String(dashboard.dashboard_id),
          name: dashboard.name || "Unnamed Dashboard",
          is_default: dashboard.is_default || false,
          position: dashboard.position || 0
        }))
        .sort((a: Dashboard, b: Dashboard) => a.position - b.position);

      // Store raw dashboard data for building update payloads (also sorted by position)
      rawDashboardsData.value = [...apiDashboards].sort((a: any, b: any) => (a.position || 0) - (b.position || 0));

      isInitialized.value = true;

      // Only set activeDashboardId if it's not already set (initial load)
      // This prevents fetchDashboards from resetting activeDashboardId during dashboard creation
      if (!activeDashboardId.value) {
        const defaultDashboard = apiDashboards.find((d: any) => d.is_default);
        if (defaultDashboard) {
          activeDashboardId.value = defaultDashboard.dashboard_id;
        } else if (dashboards.value.length > 0) {
          // If no default dashboard, set first dashboard (index 0) as default
          dashboards.value[0].is_default = true;
          rawDashboardsData.value[0].is_default = true;
          activeDashboardId.value = dashboards.value[0].id;
          logInfo(
            FEATURE_NAMES.MVS_CONTROL_TOWER,
            `No default dashboard found. Setting first dashboard as default: ${dashboards.value[0].id}`
          );
        } else {
          loading.value = false;
        }
      }

      return response as IResponse;
    } catch (err: any) {
      error.value = err?.message || "Failed to fetch dashboards";
      loading.value = false;
      throw err;
    }
  };

  const setActiveDashboard = (id: string) => {
    if (activeDashboardId.value !== id) {
      widgetsMeta.value = [];
      widgetsData.value = {};
      widgetsLoading.value = {};
      widgetsError.value = null;
      error.value = null;
      loadedWidgetsDashboardId.value = null; // Clear the loaded dashboard tracker
    }
    activeDashboardId.value = id;
    zonePrefix.value = useZonePrefix("control-tower", id);
  };

  const setActiveWidgetId = (id: string) => {
    activeWidgetId.value = id;
    widgetZonePrefix.value = useZonePrefix("control-tower", id);
  };
  const getDashboards = () => {
    return dashboards.value;
  };

  const getActiveDashboard = () => {
    return dashboards.value.find((d) => d.id === activeDashboardId.value);
  };

  const addDashboard = () => {
    isAddDashboardModalOpen.value = true;
  };

  const closeAddDashboardModal = () => {
    isAddDashboardModalOpen.value = false;
  };

  const openManageDashboardModal = () => {
    isManageDashboardModalOpen.value = true;
  };

  const closeManageDashboardModal = () => {
    isManageDashboardModalOpen.value = false;
  };

  const manageDashboards = () => {
    isManageDashboardModalOpen.value = true;
  };

  const fetchWidgetsMeta = async (dashboardId: string) => {
    // Prevent duplicate calls - if we already have widgets for this dashboard, skip
    // Check both activeDashboardId and loadedWidgetsDashboardId to ensure we're truly loaded
    if (
      widgetsMeta.value.length > 0 &&
      activeDashboardId.value === dashboardId &&
      loadedWidgetsDashboardId.value === dashboardId
    ) {
      logInfo(
        FEATURE_NAMES.MVS_CONTROL_TOWER,
        `Skipping fetchWidgetsMeta - already loaded for dashboard: ${dashboardId}`
      );
      // If skipping, ensure loading is set to false since fetchDashboardMeta won't be called
      loading.value = false;
      widgetsError.value = null;
      return;
    }

    const { fetchDashboardMeta } = useDashboardManagement();

    try {
      widgetsError.value = null; // Clear any previous errors
      const res: any = await fetchDashboardMeta(dashboardId);

      // Ignore response if a newer load was started for a different dashboard (prevents race condition)
      if (widgetsLoadDashboardId.value !== null && widgetsLoadDashboardId.value !== dashboardId) {
        logInfo(
          FEATURE_NAMES.MVS_CONTROL_TOWER,
          `Discarding fetchWidgetsMeta result for ${dashboardId} - current load is ${widgetsLoadDashboardId.value}`
        );
        return;
      }

      // Extract widgets from IResponse structure: res.data.widgets or res.widgets (handle both)
      const responseData = (res as IResponse)?.data || res;
      const widgets = responseData?.widgets || [];
      const globalFilters = responseData?.filters || {};

      // Apply global filters to the filters store
      if (globalFilters) {
        const transformedFilters = filterPayloadConverter(globalFilters);
        zonePrefix.value = useZonePrefix("control-tower", dashboardId);
        if (zonePrefix.value) {
          bulkApplyFilters(zonePrefix.value, transformedFilters);
        } else {
          logError(
            FEATURE_NAMES.MVS_CONTROL_TOWER,
            `Skipping application of global filters because zonePrefix is not set for dashboard: ${dashboardId}`
          );
        }
      } else {
        logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, `No global filters received from API for dashboard: ${dashboardId}`);
      }

      logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, `Fetched ${widgets.length} widgets for dashboard: ${dashboardId}`);

      // Log the response structure for debugging
      if (widgets.length === 0) {
        logInfo(
          FEATURE_NAMES.MVS_CONTROL_TOWER,
          `No widgets found in response. Response structure: ${JSON.stringify(Object.keys(responseData || {}))}`
        );
      }

      // Only clear and set widget state when we have new meta (avoids "add widgets" flash when switching fast)
      widgetsMeta.value = widgets;
      widgetsData.value = {};
      widgetsLoading.value = {};
      widgetsMeta.value.forEach((w: any) => {
        widgetsLoading.value[w.widget_id] = true;
      });

      // Update rawDashboardsData with the fetched widgets for this dashboard
      // This ensures that when building update payloads, we have the correct widget data
      const rawDashboardIndex = rawDashboardsData.value.findIndex((d: any) => d.dashboard_id === dashboardId);
      if (rawDashboardIndex !== -1) {
        rawDashboardsData.value[rawDashboardIndex].widgets = widgets;
        logInfo(
          FEATURE_NAMES.MVS_CONTROL_TOWER,
          `Updated rawDashboardsData with ${widgets.length} widgets for dashboard: ${dashboardId}`
        );
      }
    } catch (err: any) {
      const errorMessage = err?.message || "Failed to fetch widgets meta";
      if (widgetsLoadDashboardId.value === null || widgetsLoadDashboardId.value === dashboardId) {
        widgetsError.value = errorMessage;
      }
      logError(FEATURE_NAMES.MVS_CONTROL_TOWER, `Error fetching widgets meta: ${errorMessage}`);
      throw err; // Re-throw to let loadDashboardWidgets handle it
    } finally {
      // Set loading to false after fetchDashboardMeta completes (success or error)
      loading.value = false;
    }
  };

  /**
   * Creates a new dashboard with all widgets from catalog and activates it
   * This method handles the complete flow:
   * 1. Creates dashboard via API (backend places it at last position)
   * 2. If user selected a different position, repositions dashboards via bulk update
   * 3. Refreshes dashboards list
   * 4. Sets the new dashboard as active (triggers widget loading via watch)
   * @param dashboardName - Optional name for the dashboard (defaults to "Operations Dashboard")
   * @returns Promise<string> The created dashboard ID
   */
  const createDashboardAndActivate = async (CreateDashboardRequestBody: CreateDashboardRequest): Promise<string> => {
    const apiStart = performance.now();
    logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, `Creating dashboard and activating: ${CreateDashboardRequestBody.name}`);

    try {
      loading.value = true;
      isCreatingDashboard.value = true;
      error.value = null;

      // Step 1: Create dashboard at the requested position (backend handles repositioning internally)
      const requestedPosition = CreateDashboardRequestBody.position;
      const { createDashboard } = useCreateDashboard();

      const createPayload: CreateDashboardRequest = {
        ...CreateDashboardRequestBody,
        position: requestedPosition,
        // Backend requires is_default to be explicitly true if creating at position 1
        is_default: requestedPosition === 1
      };

      const createdDashboard = await createDashboard(createPayload);

      const newDashboardId = createdDashboard.dashboard_id;
      logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, `Dashboard created successfully: ${newDashboardId}`);

      // Fetch dashboards once at the end to get final state (avoid UI flicker)
      await fetchDashboards(true);

      // Step 3: Activate the newly created dashboard
      // Use nextTick to ensure DOM updates are complete before setting active dashboard
      await nextTick();
      setActiveDashboard(newDashboardId);

      logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, `Dashboard activated: ${newDashboardId}`);
      logMeasurement(FEATURE_NAMES.MVS_CONTROL_TOWER, METRIC_TYPE.API_CALL_LATENCY, performance.now() - apiStart, {
        api: "createDashboardAndActivate",
        dashboardName: CreateDashboardRequestBody.name
      });

      return newDashboardId;
    } catch (err: any) {
      const errorMessage = err?.message || "Failed to create dashboard";
      error.value = errorMessage;

      logEvent(FEATURE_NAMES.MVS_CONTROL_TOWER, METRIC_TYPE.API_CALL_FAILURE, {
        api: "createDashboardAndActivate",
        dashboardName: CreateDashboardRequestBody.name,
        error: errorMessage,
        statusCode: err?.response?.status
      });
      logError(FEATURE_NAMES.MVS_CONTROL_TOWER, `Error creating dashboard: ${errorMessage}`);

      throw err;
    } finally {
      loading.value = false;
      isCreatingDashboard.value = false;
    }
  };

  /**
   * Fetches widgets data in batches with controlled parallelism
   * Runs batches of widget data fetches in parallel to optimize performance
   * while avoiding overwhelming the API
   * @param forceRefresh - If true, forces a refresh of widget data even if already loaded
   * @return Promise<void>
   */
  const fetchWidgetsDataInBatches = async (forceRefresh: boolean = false, dashboardId?: string) => {
    const targetDashboardId = dashboardId || activeDashboardId.value;
    if (!targetDashboardId) {
      logError(FEATURE_NAMES.MVS_CONTROL_TOWER, "Cannot fetch widgets: No dashboard ID");
      return;
    }

    // Prevent duplicate calls - check if widgets are already loaded or loading
    const allWidgetsLoaded =
      widgetsMeta.value.length > 0 && widgetsMeta.value.every((w: any) => widgetsLoading.value[w.widget_id] === false);

    if (!forceRefresh && allWidgetsLoaded) {
      logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, `Skipping fetchWidgetsDataInBatches - widgets already loaded`);
      return;
    }

    const apiStart = performance.now();
    logInfo(
      FEATURE_NAMES.MVS_CONTROL_TOWER,
      `Starting batch widget fetch for dashboard ${targetDashboardId}. Total widgets: ${widgetsMeta.value.length}, Batch size: ${BATCH_SIZE}, Parallel batches: ${MAX_CONCURRENT_BATCHES}`
    );

    const batches = [];
    for (let i = 0; i < widgetsMeta.value.length; i += BATCH_SIZE) {
      const batch = widgetsMeta.value.slice(i, i + BATCH_SIZE);
      batches.push(batch);
    }

    for (let i = 0; i < batches.length; i += MAX_CONCURRENT_BATCHES) {
      // Re-check before each batch group to prevent race conditions
      if (targetDashboardId !== activeDashboardId.value) {
        logInfo(
          FEATURE_NAMES.MVS_CONTROL_TOWER,
          `Aborting widget fetch for dashboard ${targetDashboardId}: active dashboard changed to ${activeDashboardId.value}`
        );
        return;
      }
      const parallelBatches = batches.slice(i, i + MAX_CONCURRENT_BATCHES);
      await Promise.allSettled(parallelBatches.map((batch) => fetchWidgetBatch(batch, targetDashboardId)));
    }

    logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, `Completed batch widget fetch. Total batches: ${batches.length}`);
    logMeasurement(FEATURE_NAMES.MVS_CONTROL_TOWER, METRIC_TYPE.API_CALL_LATENCY, performance.now() - apiStart, {
      api: "widgets/fetchAllBatches",
      totalWidgets: widgetsMeta.value.length,
      totalBatches: batches.length
    });
  };

  /**
   * Gets and converts global filters for the current zone
   * @returns Converted global filters ready for API payload
   */
  const getConvertedGlobalFilters = (overrideZone?: string): Record<string, any> => {
    let zone = overrideZone || zonePrefix.value;
    let globalFilters: any = {};
    // If zone prefix is not set, construct it manually from activeDashboardId
    if (!zone && activeDashboardId.value) {
      zone = useZonePrefix("control-tower", activeDashboardId.value);
      if (!overrideZone) zonePrefix.value = zone; // Only cache if using default behaviour
    }

    if (zone) {
      const filtersForZone = (filtersApplied.value as any)[zone];
      if (filtersForZone && typeof filtersForZone === "object") {
        globalFilters = filtersForZone;
      }
    }
    return convertFilterForPayload(globalFilters);
  };
  const refreshDashboardWidgetsWithFilters = async (targetDashboardId?: string) => {
    const currentDashboardId = targetDashboardId || activeDashboardId.value;
    if (!currentDashboardId) {
      logError(FEATURE_NAMES.MVS_CONTROL_TOWER, "Cannot refresh widgets: No dashboard ID");
      return;
    }

    // If we are given a target dashboard ID that doesn't match the active one,
    // we skip the refresh because the user has navigated away.
    // The correct data will be loaded when they return to that dashboard.
    if (targetDashboardId && targetDashboardId !== activeDashboardId.value) {
      logInfo(
        FEATURE_NAMES.MVS_CONTROL_TOWER,
        `Skipping widget refresh for dashboard ${targetDashboardId} because active is ${activeDashboardId.value}`
      );
      return;
    }

    logInfo(
      FEATURE_NAMES.MVS_CONTROL_TOWER,
      `Refreshing all widgets with filters for dashboard: ${currentDashboardId}`
    );
    try {
      loading.value = true;
      await fetchWidgetsDataInBatches(true, currentDashboardId);
    } catch (error) {
      logError(
        FEATURE_NAMES.MVS_CONTROL_TOWER,
        `Error refreshing dashboard widgets for ${currentDashboardId}: ${error instanceof Error ? error.message : String(error)}`
      );
      throw error;
    } finally {
      loading.value = false;
    }
  };
  /**
   * Gets and converts local filters for the current zone
   * Reads directly from panelSelectedFilters (NOT from AppliedFilters)
   * @returns Converted local filters ready for API payload
   */
  const getConvertedLocalFilters = (
    zonePrefix: string
  ): Array<{
    key: string;
    values: any[];
  }> => {
    if (!zonePrefix) return [];
    return filtersStore.getPanelFiltersAsPayload(zonePrefix, true) as Array<{
      key: string;
      values: any[];
    }>;
  };

  /**
   * Gets and converts local configs for the current zone
   * Reads from the _config suffixed zone in panelSelectedFilters (NOT from AppliedFilters)
   * Transforms keys (bucket_ranges_0, threshold0_0) into the { type, values/ranges } format expected by the API
   * @param zonePrefix - The base zone prefix (without _config suffix)
   * @returns Converted local configs ready for API payload
   */
  const getConvertedLocalConfigs = (zonePrefix: string): Array<WidgetConfigItem> => {
    if (!zonePrefix) return [];

    const configZone = `${zonePrefix}_config`;
    const rawConfigs = filtersStore.getPanelFiltersAsPayload(configZone, true) as Array<{
      key: string;
      values: any[];
    }>;

    if (!rawConfigs || rawConfigs.length === 0) return [];

    // Separate THRESHOLD (values) and BUCKETS (ranges)
    const configs: Array<any> = [];
    const thresholdValues: string[] = [];

    rawConfigs.forEach(({ key, values }) => {
      if (key.startsWith("bucket_ranges_")) {
        configs.push({ type: "BUCKETS", ranges: values });
      } else if (key.startsWith("threshold") || key.startsWith("config")) {
        thresholdValues.push(String(values[0]));
      } else if (key.startsWith("bucket")) {
        // Fallback for old bucket logic if it exists
        configs.push({ type: "BUCKETS", values: [String(values[0])] });
      }
    });

    if (thresholdValues.length > 0) {
      configs.push({ type: "THRESHOLD", values: thresholdValues });
    }

    return configs;
  };

  const fetchWidgetBatch = async (widgets: any[], dashboardId?: string) => {
    const targetDashboardId = dashboardId || activeDashboardId.value;
    if (!targetDashboardId) return;

    // Safety check: abort if active dashboard changed during async operations
    if (dashboardId && dashboardId !== activeDashboardId.value) {
      logInfo(
        FEATURE_NAMES.MVS_CONTROL_TOWER,
        `Skipping widget batch for dashboard ${dashboardId}: active dashboard is now ${activeDashboardId.value}`
      );
      return;
    }

    const apiStart = performance.now();
    const widgetIds = widgets.map((w) => w.widget_id).join(", ");
    logInfo(
      FEATURE_NAMES.MVS_CONTROL_TOWER,
      `Fetching widget batch. Dashboard: ${targetDashboardId}, Widgets: [${widgetIds}]`
    );

    const { fetchWidgetData } = useWidgetOrchestrator();

    const convertedGlobalFilters = getConvertedGlobalFilters();
    const convertedLocalFilters = getConvertedLocalFilters(widgetZonePrefix.value);
    const convertedLocalConfigs = getConvertedLocalConfigs(widgetZonePrefix.value);
    const payload = {
      widgets: widgets.map((w) =>
        buildWidgetPayload(w, widgetCatalog.value, convertedLocalFilters, convertedLocalConfigs)
      ),
      globalFilters: convertedGlobalFilters
    };

    try {
      const apiResponse = await fetchWidgetData(targetDashboardId, payload);

      // Extract WidgetOrchestrationResponse from IResponse wrapper
      // IResponse has a data property that may contain the actual response
      const responseData = (apiResponse as IResponse)?.data || apiResponse;
      const response = responseData as WidgetOrchestrationResponse;

      // Check if results exists and is an array before processing
      if (!response || !response.results || !Array.isArray(response.results)) {
        logError(
          FEATURE_NAMES.MVS_CONTROL_TOWER,
          `Widget batch response missing results array. Expected WidgetOrchestrationResponse with results array but got: ${typeof response?.results}`
        );
        // Mark all widgets as loaded to prevent infinite loading state
        widgets.forEach((w) => {
          widgetsLoading.value[w.widget_id] = false;
        });
        return;
      }

      // Map response results back to original widgets
      response.results.forEach((item, index: number) => {
        // Try ID-based matching first
        const matchingWidget = widgets.find((w) => w.widget_id === item.widget_id);
        if (matchingWidget) {
          widgetsData.value[matchingWidget.widget_id] = item.data;
          widgetsLoading.value[matchingWidget.widget_id] = false;
        } else {
          // Try catalog_id matching as fallback
          const catalogMatch = widgets.find((w) => w.catalog_id === item.catalog_id);
          if (catalogMatch) {
            widgetsData.value[catalogMatch.widget_id] = item.data;
            widgetsLoading.value[catalogMatch.widget_id] = false;
          } else {
            // Fall back to position matching as last resort
            const originalWidget = widgets[index];
            if (originalWidget) {
              widgetsData.value[originalWidget.widget_id] = item.data;
              widgetsLoading.value[originalWidget.widget_id] = false;
            }
          }
        }
      });

      logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, `Widget batch fetched successfully. Count: ${response.results.length}`);
    } catch (err: any) {
      const errorMessage = err?.message || "Failed to fetch widget batch";

      logEvent(FEATURE_NAMES.MVS_CONTROL_TOWER, METRIC_TYPE.API_CALL_FAILURE, {
        api: "widgets/fetchBatch",
        dashboardId: activeDashboardId.value,
        widgetCount: widgets.length,
        error: errorMessage,
        statusCode: err?.response?.status
      });
      logError(FEATURE_NAMES.MVS_CONTROL_TOWER, `Error fetching widget batch: ${errorMessage}`);

      widgets.forEach((w) => {
        widgetsLoading.value[w.widget_id] = false;
      });
    } finally {
      logMeasurement(FEATURE_NAMES.MVS_CONTROL_TOWER, METRIC_TYPE.API_CALL_LATENCY, performance.now() - apiStart, {
        api: "widgets/fetchBatch",
        dashboardId: activeDashboardId.value,
        widgetCount: widgets.length
      });
    }
  };

  /**
   * Fetches the widget catalog from the API
   * @returns Promise<void>
   */
  const fetchWidgetCatalogData = async (): Promise<void> => {
    // If catalog already has items, skip fetching again
    if (widgetCatalog.value.length > 0) {
      logInfo(
        FEATURE_NAMES.MVS_CONTROL_TOWER,
        `Skipping fetchWidgetCatalogData - catalog already loaded (${widgetCatalog.value.length} items)`
      );
      return;
    }

    // If a fetch is already in flight (e.g. fast dashboard-switching triggers two concurrent
    // loadDashboardWidgets calls), await the same promise rather than returning immediately.
    // Previously this returned early and the calling loadDashboardWidgets would proceed to
    // fetchWidgetsDataInBatches with an empty widgetCatalog — causing widgets to render
    // without a catalogItem, producing wrong hyperlinkConfigs and missing widgetCatalogType
    // in the /hyperlink API payload on first click.
    if (catalogFetchPromise !== null) {
      logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, "Awaiting in-flight catalog fetch");
      return catalogFetchPromise;
    }

    isLoadingCatalog.value = true;

    const apiStart = performance.now();
    logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, "Fetching widget catalog");

    catalogFetchPromise = (async () => {
      try {
        const { fetchWidgetCatalog } = useWidgetCatalog();
        const response = await fetchWidgetCatalog();

        // Extract catalog items from response
        const responseData = response?.data || response;
        let catalogItems: WidgetCatalogItem[] = [];

        if (responseData && typeof responseData === "object") {
          // Handle response with widgets array (from API)
          if (Array.isArray((responseData as any).widgets)) {
            catalogItems = (responseData as any).widgets;
          } else if (Array.isArray(responseData)) {
            catalogItems = responseData;
          }
        }

        widgetCatalog.value = catalogItems;
        logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, `Widget catalog fetched. Count: ${catalogItems.length}`);
      } catch (err: any) {
        // Non-blocking error - widget catalog is supplementary data
        logError(FEATURE_NAMES.MVS_CONTROL_TOWER, `Error fetching widget catalog: ${err?.message}`);
      } finally {
        isLoadingCatalog.value = false;
        catalogFetchPromise = null;
        logMeasurement(FEATURE_NAMES.MVS_CONTROL_TOWER, METRIC_TYPE.API_CALL_LATENCY, performance.now() - apiStart, {
          api: "widgetCatalog/getAll"
        });
      }
    })();

    return catalogFetchPromise;
  };

  /**
   * Fetches a specific widget catalog item by catalog ID
   * First searches in already-loaded widgetCatalog array to avoid redundant API calls
   * Uses cache to avoid redundant searches
   * @param catalogId - The catalog ID of the widget to fetch
   * @returns Promise<WidgetCatalogItem | null>
   */
  const fetchWidgetCatalogDataById = async (catalogId: string): Promise<WidgetCatalogItem | null> => {
    // Search in already-loaded widget catalog array
    isLoadingCatalogById.value = true;
    try {
      if (widgetCatalog.value && widgetCatalog.value.length > 0) {
        const catalogItem = widgetCatalog.value.find((item) => item.catalog_id === catalogId);
        if (catalogItem) {
          logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, `Found catalog item in widgetCatalog array: ${catalogId}`);
          isLoadingCatalogById.value = false;
          return catalogItem;
        }
      }
    } catch (err: any) {
      logError(FEATURE_NAMES.MVS_CONTROL_TOWER, `Error searching widgetCatalog array: ${err?.message}`);
    }
    // If we reach this point, the catalog item was not found in the local cache
    logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, `Catalog item not found in widgetCatalog array: ${catalogId}`);
    isLoadingCatalogById.value = false;
    return null;
  };

  /**
   * Prefetches filter lookup data for all widgets in the dashboard
   * Fetches catalog items individually to get filter configurations,
   * then prefetches all unique lookup endpoints to "control-tower" zone
   */
  const prefetchDashboardWidgetFilters = async () => {
    const apiStart = performance.now();
    isLookupLoading.value = true;
    logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, "Prefetching filter lookups for dashboard widgets");

    try {
      // Get unique catalog IDs from widgets metadata
      const allCatalogIds = widgetsMeta.value.map((widget) => widget.catalog_id).filter((id): id is string => !!id);
      const catalogIds = Array.from(new Set(allCatalogIds));

      if (catalogIds.length === 0) {
        logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, "No widgets with catalog IDs found, skipping filter prefetch");
        return;
      }

      logInfo(
        FEATURE_NAMES.MVS_CONTROL_TOWER,
        `Fetching ${catalogIds.length} unique catalog items to prefetch filters`
      );

      // Fetch all catalog items in parallel to get filter configurations
      const catalogItems = await Promise.all(catalogIds.map((catalogId) => fetchWidgetCatalogDataById(catalogId)));

      // Filter out null and undefined results
      const validCatalogItems = catalogItems.filter((item): item is WidgetCatalogItem => item != null);

      if (validCatalogItems.length === 0) {
        logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, "No valid catalog items found, skipping filter prefetch");
        return;
      }

      logInfo(
        FEATURE_NAMES.MVS_CONTROL_TOWER,
        `Prefetching filter lookups for ${validCatalogItems.length} catalog items`
      );

      // Prefetch all filter lookups to "control-tower" zone
      await prefetchWidgetFilterLookups(validCatalogItems, "control-tower");

      logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, "Successfully prefetched all filter lookups");
    } catch (err: any) {
      // Non-blocking error - filters will still work, just slower
      logError(FEATURE_NAMES.MVS_CONTROL_TOWER, `Error prefetching widget filters: ${err?.message}`);
    } finally {
      isLookupLoading.value = false;
      logMeasurement(FEATURE_NAMES.MVS_CONTROL_TOWER, METRIC_TYPE.API_CALL_LATENCY, performance.now() - apiStart, {
        api: "prefetchDashboardWidgetFilters"
      });
    }
  };

  /**
   * Loads dashboard widgets by fetching metadata first, then
   * fetching widget catalog and widget data in parallel
   * @param dashboardId - The ID of the dashboard to load widgets for
   * @param skipCatalogFetch - If true, skips fetching widget catalog (useful when catalog is already loaded)
   * @param forceRefresh - If true, forces a refresh of widget data even if already loaded
   */
  const loadDashboardWidgets = async (
    dashboardId: string,
    skipCatalogFetch: boolean = false,
    forceRefresh: boolean = false
  ) => {
    // If widgets are already loaded for this specific dashboard, skip (unless force refresh)
    if (!forceRefresh && widgetsMeta.value.length > 0 && loadedWidgetsDashboardId.value === dashboardId) {
      const allWidgetsLoaded = widgetsMeta.value.every((w: any) => widgetsLoading.value[w.widget_id] === false);
      if (allWidgetsLoaded) {
        logInfo(
          FEATURE_NAMES.MVS_CONTROL_TOWER,
          `Skipping loadDashboardWidgets - widgets already loaded for dashboard: ${dashboardId}`
        );
        return;
      }
    }

    // When already loading, only allow a new load when switching to a different dashboard (prevents race where
    // previous dashboard's response overwrites the current one). Same-dashboard concurrent calls are skipped.
    if (isLoadingWidgets.value && widgetsLoadDashboardId.value === dashboardId) {
      logInfo(
        FEATURE_NAMES.MVS_CONTROL_TOWER,
        `Skipping loadDashboardWidgets - already loading widgets for dashboard: ${dashboardId}`
      );
      return;
    }

    const apiStart = performance.now();
    logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, `Loading dashboard widgets for: ${dashboardId}`);

    // Mark this load as the current one (latest switch wins when user changes dashboard quickly)
    widgetsLoadDashboardId.value = dashboardId;
    isLoadingWidgets.value = true;
    widgetsError.value = null;
    // Do NOT clear widgetsMeta/widgetsData here - we clear only after fetchWidgetsMeta succeeds for this dashboard
    // so we avoid flashing the "add widgets" empty state when switching fast between dashboards

    try {
      await fetchWidgetsMeta(dashboardId);

      if (widgetsLoadDashboardId.value !== dashboardId) {
        logInfo(
          FEATURE_NAMES.MVS_CONTROL_TOWER,
          `Aborting loadDashboardWidgets for ${dashboardId} - user switched to ${widgetsLoadDashboardId.value}`
        );
        return;
      }

      // Fetch widget catalog first, then widget data (sequentially)
      logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, "Starting widget catalog fetch before widget orchestration");
      isLoadingWidgets.value = false;

      // Only fetch widget catalog if not already loaded and not explicitly skipped
      if (!skipCatalogFetch) {
        await fetchWidgetCatalogData();
      } else {
        logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, "Skipping widget catalog fetch - already loaded");
      }

      if (widgetsLoadDashboardId.value !== dashboardId) {
        logInfo(
          FEATURE_NAMES.MVS_CONTROL_TOWER,
          `Aborting loadDashboardWidgets for ${dashboardId} after catalog - user switched to ${widgetsLoadDashboardId.value}`
        );
        return;
      }

      logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, "Widget catalog fetch completed; widget orchestration will follow");

      prefetchDashboardWidgetFilters().catch((err) => {
        logError(FEATURE_NAMES.MVS_CONTROL_TOWER, `Error prefetching dashboard widget filters + ${err}`);
      });
      logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, "Starting widget data fetch after catalog is loaded");
      await fetchWidgetsDataInBatches(false, dashboardId);

      if (widgetsLoadDashboardId.value !== dashboardId) {
        logInfo(
          FEATURE_NAMES.MVS_CONTROL_TOWER,
          `Aborting loadDashboardWidgets for ${dashboardId} after data - user switched to ${widgetsLoadDashboardId.value}`
        );
        return;
      }

      loadedWidgetsDashboardId.value = dashboardId;
      logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, "Widget data fetch completed");
    } catch (err) {
      if (widgetsLoadDashboardId.value === dashboardId) {
        isLoadingWidgets.value = false;
      }
      throw err;
    } finally {
      if (widgetsLoadDashboardId.value === dashboardId) {
        isLoadingWidgets.value = false;
      }
      logMeasurement(FEATURE_NAMES.MVS_CONTROL_TOWER, METRIC_TYPE.API_CALL_LATENCY, performance.now() - apiStart, {
        api: "loadDashboardWidgets",
        dashboardId
      });
    }
  };

  /**
   * Refreshes data for a single widget
   * Sets loading state, fetches data, and updates only that widget's data
   * @param widgetId - The ID of the widget to refresh
   */
  const refreshSingleWidget = async (widgetId: string, widgetZonePrefix: string) => {
    if (!activeDashboardId.value) {
      logError(FEATURE_NAMES.MVS_CONTROL_TOWER, `Cannot refresh widget ${widgetId}: No active dashboard`);
      return;
    }
    const widgetMeta = widgetsMeta.value.find((w: any) => w.widget_id === widgetId);
    if (!widgetMeta) {
      logError(FEATURE_NAMES.MVS_CONTROL_TOWER, `Widget ${widgetId} not found in metadata`);
      return;
    }

    const apiStart = performance.now();
    logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, `Refreshing single widget: ${widgetId}`);

    try {
      widgetsLoading.value[widgetId] = true;

      const convertedGlobalFilters = getConvertedGlobalFilters();
      const convertedLocalFilters = getConvertedLocalFilters(widgetZonePrefix);
      const convertedLocalConfigs = getConvertedLocalConfigs(widgetZonePrefix);
      const widgetPayload = buildWidgetPayload(
        widgetMeta,
        widgetCatalog.value,
        convertedLocalFilters,
        convertedLocalConfigs
      );

      const { fetchWidgetData } = useWidgetOrchestrator();
      const payload = {
        widgets: [widgetPayload],
        globalFilters: convertedGlobalFilters
      };

      const apiResponse = await fetchWidgetData(activeDashboardId.value, payload);

      const responseData = (apiResponse as IResponse)?.data || apiResponse;
      const response = responseData as WidgetOrchestrationResponse;

      if (response?.results && Array.isArray(response.results) && response.results.length > 0) {
        const widgetResult = response.results[0];
        widgetsData.value[widgetId] = widgetResult.data;
        logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, `Widget ${widgetId} refreshed successfully`);
      } else {
        logError(FEATURE_NAMES.MVS_CONTROL_TOWER, `No data returned for widget ${widgetId}`);
      }
    } catch (err: any) {
      const errorMessage = err?.message || `Failed to refresh widget ${widgetId}`;
      logError(FEATURE_NAMES.MVS_CONTROL_TOWER, `Error refreshing widget: ${errorMessage}`);
    } finally {
      widgetsLoading.value[widgetId] = false;
      logMeasurement(FEATURE_NAMES.MVS_CONTROL_TOWER, METRIC_TYPE.API_CALL_LATENCY, performance.now() - apiStart, {
        api: "refreshSingleWidget",
        widgetId
      });
    }
  };

  /**
   * Updates widget configuration (filters, instance_settings, thresholds, title)
   * and refreshes the widget data after successful update
   * @param widgetId - The ID of the widget to update
   * @param payload - The update request payload
   * @returns Promise<string> Success message on success
   * @throws Error with failure message on error
   */
  const updateWidgetConfigs = async (widgetId: string, payload: UpdateWidgetRequest): Promise<string> => {
    const apiStart = performance.now();

    logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, `Updating widget configuration for widget ID: ${widgetId}`);
    try {
      const widgetZonePrefix = useZonePrefix("control-tower", widgetId);
      error.value = null;
      setActiveWidgetId(widgetId);

      const { updateWidget } = useUpdateWidget();
      await updateWidget(widgetId, payload);

      const successMessage = "Widget configuration updated successfully";
      logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, `Widget configuration updated successfully for widget ID: ${widgetId}`);

      // Update widgetsMeta with the new filters/configs so they persist locally
      const widgetIndex = widgetsMeta.value.findIndex((w: any) => w.widget_id === widgetId);
      if (widgetIndex !== -1) {
        const updatedWidget = { ...widgetsMeta.value[widgetIndex] };
        updatedWidget.filters = payload.filters ?? updatedWidget.filters;
        updatedWidget.configs = payload.configs ?? updatedWidget.configs;
        widgetsMeta.value[widgetIndex] = updatedWidget;
        logInfo(
          FEATURE_NAMES.MVS_CONTROL_TOWER,
          `Updated widgetsMeta for widget ${widgetId} with configs: ${JSON.stringify(payload.configs)}`
        );
      }

      await refreshSingleWidget(widgetId, widgetZonePrefix);

      logMeasurement(FEATURE_NAMES.MVS_CONTROL_TOWER, METRIC_TYPE.API_CALL_LATENCY, performance.now() - apiStart, {
        api: "updateWidgetConfigs",
        widgetId
      });

      return successMessage;
    } catch (err: any) {
      const errorMessage = err?.message || "Failed to update widget configuration";
      error.value = errorMessage;

      logEvent(FEATURE_NAMES.MVS_CONTROL_TOWER, METRIC_TYPE.API_CALL_FAILURE, {
        api: "updateWidgetConfigs",
        widgetId,
        error: errorMessage,
        statusCode: err?.response?.status
      });
      logError(FEATURE_NAMES.MVS_CONTROL_TOWER, `Error updating widget configuration: ${errorMessage}`);

      throw new Error(errorMessage);
    }
  };

  /**
   * Updates an existing dashboard with new configuration and widgets
   * This method handles the complete flow:
   * 1. Updates dashboard via API (handles position, name, and widgets in one call)
   * 2. Refreshes dashboards list from API response
   * 3. Reloads widgets for the updated dashboard
   * @param dashboardId - The ID of the dashboard to update
   * @param updateRequest - The update request body (CreateDashboardRequest format from modal)
   * @returns Promise<void>
   */
  const updateDashboardAndRefresh = async (
    dashboardId: string,
    updateRequest: CreateDashboardRequest
  ): Promise<void> => {
    const apiStart = performance.now();
    logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, `Updating dashboard: ${dashboardId} - ${updateRequest.name}`);

    try {
      loading.value = true;
      error.value = null;

      // Check if position changed to handle repositioning of other dashboards
      const currentDashboard = dashboards.value.find((d) => d.id === dashboardId);
      const currentPosition = currentDashboard?.position || 0;
      const newPosition = updateRequest.position;
      const positionChanged = newPosition !== undefined && newPosition !== currentPosition;

      logInfo(
        FEATURE_NAMES.MVS_CONTROL_TOWER,
        positionChanged
          ? `Position changed from ${currentPosition} to ${newPosition}`
          : `No position change (position: ${currentPosition})`
      );

      // Calculate position updates for all dashboards if position changed
      const positionMap: Map<string, number> = new Map();

      if (positionChanged) {
        const { repositionDashboards } = useDashboardRepositioning();
        const dashboardUpdates = repositionDashboards({
          action: "reposition",
          dashboards: dashboards.value,
          repositionDashboardId: dashboardId,
          fromPosition: currentPosition,
          targetPosition: newPosition
        });

        // Build a map of dashboard IDs to their new positions
        dashboardUpdates.forEach((update) => {
          positionMap.set(update.dashboard_id, update.position);
        });

        logInfo(
          FEATURE_NAMES.MVS_CONTROL_TOWER,
          `Repositioning ${dashboardUpdates.length} dashboard(s): ${Array.from(positionMap.entries())
            .map(([id, pos]) => `${id}→${pos}`)
            .join(", ")}`
        );
      }

      // Build the widget management payload with ONLY the dashboards that changed
      // This includes: the selected dashboard + any dashboards affected by repositioning
      const changedDashboards = rawDashboardsData.value
        .map((rawDashboard: any) => {
          const isSelectedDashboard = rawDashboard.dashboard_id === dashboardId;
          const hasPositionChange = positionMap.has(rawDashboard.dashboard_id);

          // Only include dashboards that changed
          if (!isSelectedDashboard && !hasPositionChange) {
            return null; // Skip unchanged dashboards
          }

          // Determine the position: use repositioning result if available, otherwise keep current
          const finalPosition = positionMap.has(rawDashboard.dashboard_id)
            ? positionMap.get(rawDashboard.dashboard_id)!
            : rawDashboard.position || 0;

          if (isSelectedDashboard) {
            // For the selected dashboard, use the new name and widgets from the update request
            return {
              dashboard_id: dashboardId,
              name: updateRequest.name,
              position: finalPosition,
              config: {
                layout_type: updateRequest.config?.layout_type || rawDashboard.config?.layout_type || "GRID",
                theme: rawDashboard.config?.theme || "",
                refresh_interval: rawDashboard.config?.refresh_interval || 0
              },
              is_default: finalPosition === 1, // Position 1 is always default
              widgets: updateRequest.widgets.map((widget, index) => ({
                widget_id: "",
                catalog_id: widget.catalog_id,
                title: widget.title,
                layout: {
                  x: widget.layout?.x || 0,
                  y: widget.layout?.y || 0,
                  sequence: widget.layout?.sequence || index + 1
                },
                config: [],
                instance_settings: {
                  display: ""
                },
                filters: []
              }))
            };
          } else {
            // For other dashboards affected by repositioning, send minimal data (position only)
            return {
              dashboard_id: rawDashboard.dashboard_id,
              name: rawDashboard.name,
              position: finalPosition,
              config: {
                layout_type: rawDashboard.config?.layout_type || "GRID",
                theme: rawDashboard.config?.theme || "",
                refresh_interval: rawDashboard.config?.refresh_interval || 0
              },
              is_default: finalPosition === 1, // Position 1 is always default
              widgets: (rawDashboard.widgets || []).map((w: any, index: number) => ({
                widget_id: w.widget_id || "",
                catalog_id: w.catalog_id || "",
                title: w.title || "",
                layout: {
                  x: w.layout?.x || 0,
                  y: w.layout?.y || 0,
                  sequence: w.layout?.sequence || index + 1
                },
                config: w.config || [],
                instance_settings: w.instance_settings || { display: "" },
                filters: w.filters || []
              }))
            };
          }
        })
        .filter((dashboard) => dashboard !== null); // Remove null entries

      const widgetManagementPayload: WidgetManagementRequest = {
        dashboards: changedDashboards as any
      };

      logInfo(
        FEATURE_NAMES.MVS_CONTROL_TOWER,
        `Payload built with ${widgetManagementPayload.dashboards.length} dashboards`
      );

      // Update dashboard using the composable
      const { updateDashboardWidgets } = useDashboardManagement();
      const updateResponse = await updateDashboardWidgets(dashboardId, widgetManagementPayload);

      logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, `Dashboard updated successfully: ${dashboardId}`);

      // Merge the response from update API with existing dashboard data
      const responseData = (updateResponse as IResponse)?.data || updateResponse;
      if (responseData && responseData.dashboards && Array.isArray(responseData.dashboards)) {
        const updatedDashboardsFromAPI = responseData.dashboards;

        logInfo(
          FEATURE_NAMES.MVS_CONTROL_TOWER,
          `API returned ${updatedDashboardsFromAPI.length} updated dashboard(s)`
        );

        // Create a map of updated dashboards by ID for quick lookup
        const updatedDashboardMap = new Map(updatedDashboardsFromAPI.map((d: any) => [d.dashboard_id, d]));

        // Merge: Update changed dashboards, keep unchanged ones as-is
        const mergedRawDashboards = rawDashboardsData.value.map((existingDashboard: any) => {
          const updated = updatedDashboardMap.get(existingDashboard.dashboard_id);
          return updated || existingDashboard; // Use updated if available, otherwise keep existing
        });

        // Update raw dashboard data with merged result (sorted by position)
        rawDashboardsData.value = mergedRawDashboards.sort((a: any, b: any) => (a.position || 0) - (b.position || 0));

        // Update dashboards list for UI (sorted by position)
        dashboards.value = mergedRawDashboards
          .map((dashboard: any) => ({
            id: dashboard.dashboard_id || String(dashboard.dashboard_id),
            name: dashboard.name || "Unnamed Dashboard",
            is_default: dashboard.is_default || false,
            position: dashboard.position || 0
          }))
          .sort((a: Dashboard, b: Dashboard) => a.position - b.position);

        logInfo(
          FEATURE_NAMES.MVS_CONTROL_TOWER,
          `State updated with ${dashboards.value.length} total dashboards (${updatedDashboardsFromAPI.length} changed)`
        );
      }

      // Reload widgets ONLY for the updated dashboard after successful update
      // Skip catalog fetch only when catalog is already available.
      // For new/empty dashboards, the first widget add can happen before catalog is populated;
      // forcing skip in that case causes widgets to render without catalog metadata and can
      // produce empty/incorrect hyperlinkConfigs on first click.
      if (activeDashboardId.value === dashboardId) {
        // Reset widget state to force reload
        widgetsMeta.value = [];
        widgetsData.value = {};
        widgetsLoading.value = {};
        const shouldSkipCatalogFetch = widgetCatalog.value.length > 0;
        await loadDashboardWidgets(dashboardId, shouldSkipCatalogFetch);
        logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, `Widgets reloaded for updated dashboard: ${dashboardId}`);
      }

      // Close the manage dashboard modal after successful update
      closeManageDashboardModal();

      logMeasurement(FEATURE_NAMES.MVS_CONTROL_TOWER, METRIC_TYPE.API_CALL_LATENCY, performance.now() - apiStart, {
        api: "updateDashboardAndRefresh",
        dashboardId,
        dashboardName: updateRequest.name
      });
    } catch (err: any) {
      const errorMessage = err?.message || "Failed to update dashboard";
      error.value = errorMessage;

      logEvent(FEATURE_NAMES.MVS_CONTROL_TOWER, METRIC_TYPE.API_CALL_FAILURE, {
        api: "updateDashboardAndRefresh",
        dashboardId,
        dashboardName: updateRequest.name,
        error: errorMessage,
        statusCode: err?.response?.status
      });
      logError(FEATURE_NAMES.MVS_CONTROL_TOWER, `Error updating dashboard: ${errorMessage}`);

      throw err;
    } finally {
      loading.value = false;
    }
  };

  const $reset = () => {
    dashboards.value = [];
    rawDashboardsData.value = [];
    loadedWidgetsDashboardId.value = null;
    loading.value = false;
    error.value = null;
    isCreatingDashboard.value = false;
    isDeletingDashboard.value = false;
    isInitialized.value = false;
    activeDashboardId.value = null;

    widgetsMeta.value = [];
    widgetsData.value = {};
    widgetsLoading.value = {};
    widgetCatalog.value = [];
    isLoadingWidgets.value = false;
    widgetsError.value = null;
  };

  /**
   * Persists the widget order to the backend API
   * This is called by the debounced reorder function
   * @param dashboardId - The ID of the dashboard
   * @param widgetIds - Array of widget IDs in the new desired order
   */
  const persistWidgetOrder = async (dashboardId: string, widgetIds: string[]) => {
    try {
      logInfo(
        FEATURE_NAMES.MVS_CONTROL_TOWER,
        `Persisting widget order for dashboard ${dashboardId}. Widget IDs: [${widgetIds.join(", ")}]`
      );

      // Build array of widget objects with widget_id and layout for API
      // Preserve existing x, y coordinates and only update sequence
      const widgetObjects = widgetIds.map((widgetId, index) => {
        const widget = widgetsMeta.value.find((w) => w.widget_id === widgetId);
        return {
          widget_id: widgetId,
          layout: {
            ...widget?.layout,
            sequence: index + 1 // Update sequence based on new order
          }
        };
      });

      const { updateWidgetsOrder } = useDashboardManagement();
      await updateWidgetsOrder(dashboardId, widgetObjects);

      logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, `Widget order persisted successfully for dashboard ${dashboardId}`);
    } catch (err: any) {
      const errorMessage = err?.message || "Failed to persist widget order";
      logError(
        FEATURE_NAMES.MVS_CONTROL_TOWER,
        `Error persisting widget order for dashboard ${dashboardId}: ${errorMessage}`
      );
      // Note: We don't throw here to avoid interrupting the user experience
      // The local state is already updated, so the UI remains consistent
    }
  };

  /**
   * Debounced version of persistWidgetOrder
   * Waits for REORDER_DEBOUNCE_MS milliseconds of inactivity before making the API call
   */
  const debouncedPersistWidgetOrder = debounce(persistWidgetOrder, REORDER_DEBOUNCE_MS);

  /**
   * Reorders widgets based on the new order provided
   * Updates the widgetsMeta array to reflect the new positions
   * and persists the changes to the backend API asynchronously with debouncing
   * @param newOrder - Array of widget IDs in the new desired order
   */
  const reorderWidgets = (newOrder: string[]) => {
    try {
      logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, `Reordering widgets for dashboard: ${activeDashboardId.value}`);

      // Create a map of existing widgets for quick lookup
      const widgetMap = new Map(widgetsMeta.value.map((widget) => [widget.widget_id, widget]));

      // Reorder widgets based on the newOrder array and update their position and layout values
      // The sequence property determines the widget position in the grid
      const reorderedWidgets = newOrder
        .map((widgetId, index) => {
          const widget = widgetMap.get(widgetId);
          if (widget) {
            // Update the position and layout sequence to reflect the new order
            return {
              ...widget,
              layout: {
                sequence: index + 1 // Overall position (1-indexed)
              }
            };
          }
          return undefined;
        })
        .filter((widget) => widget !== undefined);

      // Update the widgetsMeta with the new order and positions immediately (optimistic update)
      widgetsMeta.value = reorderedWidgets;

      logInfo(
        FEATURE_NAMES.MVS_CONTROL_TOWER,
        `Successfully reordered ${reorderedWidgets.length} widgets locally with updated positions`
      );

      // Persist to backend asynchronously with debouncing
      // This will wait for REORDER_DEBOUNCE_MS after the last reorder before making the API call
      if (activeDashboardId.value) {
        debouncedPersistWidgetOrder(activeDashboardId.value, newOrder);
      }
    } catch (err: any) {
      const errorMessage = err?.message || "Failed to reorder widgets";
      logError(FEATURE_NAMES.MVS_CONTROL_TOWER, `Error reordering widgets: ${errorMessage}`);
    }
  };

  /**
   * Deletes a dashboard and repositions remaining dashboards
   * This method handles the complete flow:
   * 1. Repositions dashboard to last position (moves it out of the way)
   * 2. Deletes the dashboard (now at last position)
   * 3. Removes dashboard from local state only if delete is successful
   * @param dashboardId - The ID of the dashboard to delete
   * @returns Promise<null>
   */
  const deleteDashboard = async (dashboardId: string): Promise<null> => {
    const apiStart = performance.now();

    try {
      loading.value = true;
      isDeletingDashboard.value = true;
      error.value = null;

      // Remember if we're deleting the active dashboard
      const wasActiveDashboard = activeDashboardId.value === dashboardId;

      // Step 1: Reposition dashboard to last position BEFORE deleting
      const { repositionDashboards } = useDashboardRepositioning();
      const { bulkUpdateDashboards } = useDashboardManagement();

      // Calculate which dashboards need position updates to move target to end
      const dashboardUpdates = repositionDashboards({
        action: "delete",
        dashboards: dashboards.value,
        deleteDashboardId: dashboardId
      });

      // Only call bulk update if there are dashboards to update (target is not already at last position)
      if (dashboardUpdates.length > 0) {
        logInfo(
          FEATURE_NAMES.MVS_CONTROL_TOWER,
          `Step 1: Moving dashboard ${dashboardId} to last position before deletion`
        );

        // Build full payload with widgets for affected dashboards
        const repositionPayload = {
          dashboards: dashboardUpdates.map((update) => {
            const rawDashboard = rawDashboardsData.value.find((d: any) => d.dashboard_id === update.dashboard_id);
            return {
              dashboard_id: update.dashboard_id,
              name: rawDashboard?.name || "",
              position: update.position,
              config: rawDashboard?.config || { layout_type: "GRID", theme: "", refresh_interval: 0 },
              is_default: update.is_default !== undefined ? update.is_default : rawDashboard?.is_default || false,
              widgets: (rawDashboard?.widgets || []).map((w: any, index: number) => ({
                widget_id: w.widget_id || "",
                catalog_id: w.catalog_id || "",
                title: w.title || "",
                layout: {
                  x: w.layout?.x || 0,
                  y: w.layout?.y || 0,
                  sequence: w.layout?.sequence || index + 1
                },
                config: w.config || [],
                instance_settings: w.instance_settings || { display: "" },
                filters: w.filters || []
              }))
            };
          })
        };

        await bulkUpdateDashboards(repositionPayload);
        logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, "Step 1 complete: Dashboard repositioned to last position");
      }

      // Step 2: Delete dashboard via API
      logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, `Step 2: Deleting dashboard ${dashboardId}`);
      const { deleteDashboard: deleteDashboardFromComposable } = useDashboardManagement();
      await deleteDashboardFromComposable(dashboardId);
      logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, `Step 2 complete: Dashboard deleted successfully`);

      // Step 3: Fetch final state after deletion
      logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, `Step 3: Fetching updated dashboard list`);
      await fetchDashboards(true);
      logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, `Step 3 complete: ${dashboards.value.length} dashboards remaining`);

      // Step 4: If the deleted dashboard was active, activate first dashboard
      if (wasActiveDashboard) {
        if (dashboards.value.length > 0) {
          const firstDashboard = dashboards.value[0];
          setActiveDashboard(firstDashboard.id);
          logInfo(
            FEATURE_NAMES.MVS_CONTROL_TOWER,
            `Activated first dashboard (${firstDashboard.name}) after deleting active dashboard`
          );
        } else {
          activeDashboardId.value = null;
          logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, "No dashboards remaining after deletion");
        }
      }

      logMeasurement(FEATURE_NAMES.MVS_CONTROL_TOWER, METRIC_TYPE.API_CALL_LATENCY, performance.now() - apiStart, {
        api: "deleteDashboard",
        dashboardId
      });

      return null;
    } catch (err: any) {
      const errorMessage = err?.message || `Failed to delete dashboard ID: ${dashboardId}`;
      error.value = errorMessage;

      logEvent(FEATURE_NAMES.MVS_CONTROL_TOWER, METRIC_TYPE.API_CALL_FAILURE, {
        api: "deleteDashboard",
        dashboardId,
        error: errorMessage,
        statusCode: err?.response?.status
      });
      logError(FEATURE_NAMES.MVS_CONTROL_TOWER, `Error deleting dashboard: ${errorMessage}`);

      throw err;
    } finally {
      loading.value = false;
      isDeletingDashboard.value = false;
    }
  };

  const getWidgetHyperlink = async ({ widgetId, hyperlinkConfigs }) => {
    // Find the widget meta first with defensive check
    const widgetMeta = widgetsMeta.value.find((w: any) => w.widget_id === widgetId);

    if (!widgetMeta) {
      const errorMessage = `Widget not found in widgetsMeta for widgetId: ${widgetId}`;
      logError(FEATURE_NAMES.MVS_CONTROL_TOWER, errorMessage);
      throw new Error(errorMessage);
    }

    // Find the widget catalog type
    const catalogItem = widgetCatalog.value.find((w: any) => w.catalog_id === widgetMeta.catalog_id);
    const widgetCatalogType = catalogItem?.widget_catalog_type;

    const { getWidgetHyperlink } = useWidgetOrchestrator();

    // Use buildWidgetPayload for consistent config/filter resolution (local → saved → catalog defaults)
    // This matches the same resolution chain used by fetchWidgetBatch and refreshSingleWidget
    const widgetZonePrefixValue = useZonePrefix("control-tower", widgetId);
    const convertedLocalFilters = getConvertedLocalFilters(widgetZonePrefixValue);
    const convertedLocalConfigs = getConvertedLocalConfigs(widgetZonePrefixValue);
    const widgetPayload = buildWidgetPayload(
      widgetMeta,
      widgetCatalog.value,
      convertedLocalFilters,
      convertedLocalConfigs
    );

    const payload = {
      widgetId: widgetId,
      configs: widgetPayload.configs || [],
      filters: widgetPayload.filters || [],
      globalFilters: getConvertedGlobalFilters(),
      hyperlinkConfigs,
      widgetCatalogType
    };

    try {
      const hyperlink = await getWidgetHyperlink(payload);
      return hyperlinkEnricher(hyperlink as any);
    } catch (err: any) {
      const errorMessage = err?.message || "Failed to fetch widget hyperlink";
      logError(FEATURE_NAMES.MVS_CONTROL_TOWER, `Error getting widget hyperlink: ${errorMessage}`);
      throw new Error(errorMessage);
    }
  };

  const updateGlobalFilters = async (targetDashboardId?: string, targetZone?: string) => {
    // Determine target dashboard ID and zone
    const currentDashboardId = targetDashboardId || activeDashboardId.value;

    // get filters in converted format with defensive default, using the specific target zone if provided
    const convertedGlobalFilters = getConvertedGlobalFilters(targetZone) ?? {};
    const { updateDashboardFilters } = useDashboardManagement();

    if (!currentDashboardId) {
      const errorMessage = "No active dashboard ID found";
      logError(FEATURE_NAMES.MVS_CONTROL_TOWER, `Error updating dashboard filters: ${errorMessage}`);
      throw new Error(errorMessage);
    }

    const payload = { filters: convertedGlobalFilters };

    try {
      const res = await updateDashboardFilters(currentDashboardId, payload);
      logInfo(
        FEATURE_NAMES.MVS_CONTROL_TOWER,
        `Dashboard filters updated successfully for ${currentDashboardId}, response: ${JSON.stringify(res)}`
      );
      return res;
    } catch (error: any) {
      const errorMessage = error?.message || "Failed to update dashboard filters";
      logError(
        FEATURE_NAMES.MVS_CONTROL_TOWER,
        `Error updating dashboard filters for ${currentDashboardId}: ${errorMessage}`
      );
      throw new Error(`Failed to save dashboard filters: ${errorMessage}`);
    }
  };
  return {
    activeDashboardId,
    dashboards,
    loading,
    error,
    isCreatingDashboard,
    isDeletingDashboard,
    isAddDashboardModalOpen,
    isManageDashboardModalOpen,
    setActiveDashboard,
    getDashboards,
    getActiveDashboard,
    addDashboard,
    closeAddDashboardModal,
    openManageDashboardModal,
    closeManageDashboardModal,
    isInitialized,
    manageDashboards,
    fetchDashboards,
    updateDashboardAndRefresh,
    rawDashboardsData,

    widgetsMeta,
    widgetsData,
    widgetsLoading,
    widgetCatalog,
    isLoadingCatalogById,
    isLoadingWidgets,
    widgetsError,
    loadDashboardWidgets,
    fetchWidgetsMeta,
    fetchWidgetCatalogDataById,
    createDashboardAndActivate,
    selectedWidgetCatalog,
    reorderWidgets,
    updateWidgetConfigs,
    refreshSingleWidget,
    refreshDashboardWidgetsWithFilters,
    $reset,
    deleteDashboard,
    getWidgetHyperlink,
    updateGlobalFilters,
    isLookupLoading
  };
});
